import { IMarker } from '../models/marker.model';

export class MarkerUtil {
  /**
   * Get absolute X coordinate from relative position
   */
  static getAbsoluteX(marker: IMarker, width: number): number {
    return Math.round(marker.relativeX * width);
  }

  /**
   * Get absolute Y coordinate from relative position
   */
  static getAbsoluteY(marker: IMarker, height: number): number {
    return Math.round(marker.relativeY * height);
  }

  /**
   * Get client X coordinate with scaling and offset adjustments
   */
  static getClientX(
    marker: IMarker,
    client_w: number,
    client_h: number,
    nat_w: number,
    nat_h: number,
    offset = 0
  ): number {
    const ratio_w = client_w / nat_w;
    const ratio_h = client_h / nat_h;
    const ratio = ratio_w > ratio_h ? ratio_w : ratio_h;
    const offset_w = ratio_w < ratio_h ? -(nat_w * ratio_h - client_w) * 0.5 : 0;

    let x = this.getAbsoluteX(marker, nat_w) * ratio + offset_w;

    const MARKER_OFFSET = offset;
    if (x < MARKER_OFFSET) x = MARKER_OFFSET;
    if (x > client_w - MARKER_OFFSET) x = client_w - MARKER_OFFSET;

    return x;
  }

  /**
   * Get client Y coordinate with scaling and offset adjustments
   */
  static getClientY(
    marker: IMarker,
    client_w: number,
    client_h: number,
    nat_w: number,
    nat_h: number,
    offset = 0
  ): number {
    const ratio_w = client_w / nat_w;
    const ratio_h = client_h / nat_h;
    const ratio = ratio_w > ratio_h ? ratio_w : ratio_h;
    const offset_h = ratio_w > ratio_h ? -(nat_h * ratio_w - client_h) * 0.5 : 0;

    let y = this.getAbsoluteY(marker, nat_h) * ratio + offset_h;

    const MARKER_OFFSET = offset;
    if (y < MARKER_OFFSET) y = MARKER_OFFSET;
    if (y > client_h - MARKER_OFFSET) y = client_h - MARKER_OFFSET + 5; // +5px is a fixed amount so the marker actually fits nicely on the bottom side

    return y;
  }

  /**
   * Get absolute height from relative height
   */
  static getAbsoluteHeight(marker: IMarker, height: number): number {
    return Math.round(marker.relativeHeight * height);
  }

  /**
   * Get absolute width from relative width
   */
  static getAbsoluteWidth(marker: IMarker, width: number): number {
    return Math.round(marker.relativeWidth * width);
  }

  /**
   * Get absolute diameter from relative diameter
   */
  static getAbsoluteDiameter(marker: IMarker, width: number): number {
    return Math.round(marker.relativeDiameter * width);
  }

  /**
   * Set absolute X coordinate and convert to relative
   */
  static setAbsoluteX(marker: IMarker, x: number, width: number): void {
    marker.relativeX = x > 0 ? x / width : 0;
  }

  /**
   * Set absolute Y coordinate and convert to relative
   */
  static setAbsoluteY(marker: IMarker, y: number, height: number): void {
    marker.relativeY = y > 0 ? y / height : 0;
  }

  /**
   * Set client X coordinate with inverse scaling and offset
   */
  static setClientX(
    marker: IMarker,
    x: number,
    client_w: number,
    client_h: number,
    nat_w: number,
    nat_h: number
  ): void {
    const ratio_w = client_w / nat_w;
    const ratio_h = client_h / nat_h;
    const ratio = ratio_w > ratio_h ? ratio_w : ratio_h;
    const offset_w = ratio_w < ratio_h ? -(nat_w * ratio_h - client_w) * 0.5 : 0;

    this.setAbsoluteX(marker, (x - offset_w) / ratio, nat_w);
  }

  /**
   * Set client Y coordinate with inverse scaling and offset
   */
  static setClientY(
    marker: IMarker,
    y: number,
    client_w: number,
    client_h: number,
    nat_w: number,
    nat_h: number
  ): void {
    const ratio_w = client_w / nat_w;
    const ratio_h = client_h / nat_h;
    const ratio = ratio_w > ratio_h ? ratio_w : ratio_h;
    const offset_h = ratio_w > ratio_h ? -(nat_h * ratio_w - client_h) * 0.5 : 0;

    this.setAbsoluteY(marker, (y - offset_h) / ratio, nat_h);
  }

  /**
   * Set absolute diameter and convert to relative
   */
  static setAbsoluteDiameter(marker: IMarker, d: number, width: number): void {
    marker.relativeDiameter = d > 0 ? d / width : 0;
  }

  /**
   * Create a deselected empty marker
   */
  static getDeselectedEmptyMarker(): IMarker {
    return {
      sensorID: -1,
      label: '',
      relativeX: 0.5,
      relativeY: 0.5,
      relativeDiameter: 0.1,
      selected: false,
      active: false,
      circular: true,
      relativeWidth: 0,
      relativeHeight: 0,
    };
  }

  /**
   * Create a marker with default values
   */
  static createMarker(
    sensorID = -1,
    label = '',
    relativeX = 0.5,
    relativeY = 0.5,
    relativeDiameter = 0.1,
    selected = true,
    width = 0,
    height = 0,
    relativeWidth = 0,
    relativeHeight = 0
  ): IMarker {
    const marker: IMarker = {
      sensorID,
      label,
      relativeDiameter,
      selected,
      relativeX: 0,
      relativeY: 0,
      active: false,
      circular: true,
      relativeWidth: 0,
      relativeHeight: 0,
    };

    if (width > 0 || height > 0) {
      this.setAbsoluteX(marker, relativeX, width);
      this.setAbsoluteY(marker, relativeY, height);
    } else {
      marker.relativeX = relativeX;
      marker.relativeY = relativeY;
    }

    if (relativeWidth > 0 || relativeHeight > 0) {
      marker.circular = false;
      marker.relativeWidth = relativeWidth;
      marker.relativeHeight = relativeHeight;
    }

    return marker;
  }
}
